import { ref } from "vue";
import * as Sentry from "@sentry/vue";

import {
  Country_Enum,
  GetLibraryCountryDatasourcesDocument,
  type GetLibraryCountryDatasourcesQuery,
  type GetLibraryCountryDatasourcesQueryVariables,
} from "@/graphql";
import gaiupApolloClient from "@/graphql/services/ApolloClient";
import { getDataSources } from "@/api/services/Materials";
import { organizationsStore } from "@/stores/Utils/auth/Organizations";
import {
  buildCountryDatasourceMap,
  isAllowedIn,
  mapDatasourcesToNames,
  mergeCountryDatasourceFilter,
  pruneDisallowedDatasources,
  splitDatasourcesByAllowed,
  type CountryDatasourceMap,
} from "./libraryCountryDatasourcesUtils";

// Re-export the pure helpers so callers can import everything from the composable.
// They live in a separate Apollo-free module so unit tests can exercise them
// without loading the GraphQL/WebSocket runtime.
export {
  buildCountryDatasourceMap,
  isAllowedIn,
  mapDatasourcesToNames,
  mergeCountryDatasourceFilter,
  pruneDisallowedDatasources,
  splitDatasourcesByAllowed,
};
export type { CountryDatasourceMap };

// Shared module-level state: the mapping is tenant reference data that barely changes, and the
// toolbar that reads it remounts on every Materials/Constructions tab switch and every entry into
// Material Mapping — fetching per mount fired the two requests constantly for the same answer
// (RTLCA-5161). One copy, loaded lazily on first use, shared by every mount.
const datasourceNamesByCountry = ref<CountryDatasourceMap>({});
const countries = ref<Country_Enum[]>([]);
const isLoading = ref(true);
const error = ref(false);

// Which tenant the held data belongs to, and the in-flight load consumers share. A tenant switch
// does NOT reload the app (OrganizationsDropdown resets Apollo and moves on), so the cache keys on
// the tenant and refetches when a mount finds it stale.
let loadedForTenantId: string | null | undefined;
let hasLoaded = false;
let inFlight: Promise<void> | null = null;

const currentTenantId = () => organizationsStore().getCurrentTenant()?.tenantId ?? null;

const load = async () => {
  const tenantId = currentTenantId();
  isLoading.value = true;
  error.value = false;
  try {
    const [result, datasourceList] = await Promise.all([
      gaiupApolloClient.query<GetLibraryCountryDatasourcesQuery, GetLibraryCountryDatasourcesQueryVariables>({
        query: GetLibraryCountryDatasourcesDocument,
        fetchPolicy: "network-only",
      }),
      getDataSources(),
    ]);

    // A non-array here means the REST api/DataSource call failed; the country
    // filter then can't resolve any names and silently offers no flags, so
    // surface it for observability (the material list still works unfiltered).
    const datasources = Array.isArray(datasourceList) ? datasourceList : [];
    if (!Array.isArray(datasourceList)) {
      Sentry.captureMessage("Library country sidebar: api/DataSource request failed; country filter unavailable");
    }

    const nameById: Record<string, string> = {};
    for (const datasource of datasources) {
      if (datasource?.id && datasource?.name) nameById[String(datasource.id)] = String(datasource.name);
    }

    const nameMap = mapDatasourcesToNames(buildCountryDatasourceMap(result.data), nameById);
    datasourceNamesByCountry.value = nameMap;
    // Keep the enum declaration order so the flag list is stable across reloads.
    countries.value = (Object.values(Country_Enum) as Country_Enum[]).filter((country) => nameMap[country]?.length);
    hasLoaded = true;
    // A switch that lands mid-load would leave this data fetched under the OLD tenant's token but
    // filed under the new one. Record the tenant only if it is still the one the load started
    // under; otherwise leave the cache marked stale so the next consumer refetches.
    loadedForTenantId = tenantId === currentTenantId() ? tenantId : undefined;
  } catch (e) {
    error.value = true;
    Sentry.captureException(e);
    datasourceNamesByCountry.value = {};
    countries.value = [];
    hasLoaded = false;
  } finally {
    isLoading.value = false;
    inFlight = null;
  }
};

// Fetch only when nothing usable is held: never fetched, a previous failure (retried so one
// transient error doesn't stick for the session), or data belonging to another tenant.
// Concurrent consumers share one request.
const ensureLoaded = (): Promise<void> => {
  if (inFlight) return inFlight;
  if (hasLoaded && !error.value && loadedForTenantId === currentTenantId()) return Promise.resolve();
  inFlight = load();
  return inFlight;
};

const reload = (): Promise<void> => {
  if (inFlight) return inFlight;
  inFlight = load();
  return inFlight;
};

// The Library "Allowed EPDs" country -> datasource mapping, shared across mounts.
// Combines two sources: Hasura (country -> epdDatasourceId uuids, via the direct
// Apollo client per the project's GraphQL rules for composables) and the REST
// api/DataSource list (uuid -> datasource name). The material search filters
// datasources by NAME, so the country -> uuid map is translated to names — the
// same identifier space the toolbar datasource filter and backend use.
// Fails safe: on error the sidebar shows no flags and the material list keeps
// working unfiltered.
export const useLibraryCountryDatasources = () => {
  // Datasource filter values (NAMES) for the selected country, matching the id
  // space the toolbar datasource filter and material search use.
  const getDatasourceFilterValues = (country: Country_Enum | null): string[] =>
    country ? datasourceNamesByCountry.value[country] ?? [] : [];

  void ensureLoaded();

  return { countries, datasourceNamesByCountry, isLoading, error, getDatasourceFilterValues, reload };
};
